import React, { useRef, useState, useContext } from 'react';
import t from 'react-translate';
import { AngularServicesContext } from 'react-app';
import { isEmpty } from 'underscore';

// redux
import { useAppDispatch } from 'redux/store';
import { editQuizQuestion } from 'redux/actions/quizzes';
import { useSelector } from 'react-redux';
import { getQuizQuestion } from 'redux/selectors/quizzes';

import NvFroala from 'froala/components/nv-froala';
import { SavingRegistryContext } from 'shared/hooks/use-saving-registry';
import QuestionSetContext, { QuestionContext, SavingIndicator } from 'lecture_pages/services/question-set-context';
import ResponsivelyEmbeddedAngularHTML from 'shared/components/responsively-embedded-angular-html';
import useQuestionSetHelpers from 'quizzes/hooks/use-question-set-helpers';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import { FroalaViewMode, UploadType } from 'froala/helpers/nv-froala-constants';
import { ComponentType } from 'redux/schemas/models/lecture-component';
import { renderToStaticMarkup } from 'react-dom/server';

type Props = {
  className?: string;
};

const defaultQuestionTemplate = '<p class="froala-style-medium"><br /></p>';

const RequiredAsterisk = () => (
  <span className='ml-1'>
    <span
      className='text-danger text-small text-weight-normal'
      uib-tooltip={t.LECTURE_PAGES.COMPONENTS.UPDATE_COURSE_COLOR.TOOLTIP_TEXT.TOOLTIP_TEXT_QUIZ_REQUIRED()}
      tooltip-append-to-body='true'
      tooltip-placement='auto top'
      tooltip-class='tooltip-top'
    >
      *
    </span>
  </span>
);

const QuizQuestionContent = (props: Props) => {
  const { className } = props;

  const dispatch = useAppDispatch();
  const mountedRef = useRef(false);
  const {
    savingIndicatorTimeoutRef,
    setSavingStatus,
  } = useContext(QuestionSetContext);

  const {
    isEditMode,
    getQuestionLabelElementId,
    dataQaQuestionSetAttributes,
    isCurrentActivityType,
  } = useQuestionSetHelpers();

  const { currentQuestion } = useContext(QuestionContext);
  const angularServices = useContext(AngularServicesContext);
  const { registerSaving } = useContext(SavingRegistryContext);
  const [value, setValue] = useState(currentQuestion.questionText || defaultQuestionTemplate);
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [imageSrc, setImageSrc] = useState();
  const childrenQuestions = useSelector((state) => {
    if (currentQuestion.childrenQuestions && currentQuestion.childrenQuestions.length > 0) {
      return currentQuestion.childrenQuestions.map((childQuestion) => getQuizQuestion(state, childQuestion));
    }
    return [];
  });
  const isCurrentQuestionRequired = () => {
    if (childrenQuestions.length > 0) {
      // marking current question as required if any of its children questions are required
      return childrenQuestions.some((childQuestion) => childQuestion.isRequired);
    }
    return currentQuestion.isRequired;
  };


  const handleChange = (html) => {
    const patch = {
      questionText: html,
    };
    if (value !== html) {
      setValue(html);
      const unregister = registerSaving();
      setSavingStatus(SavingIndicator.SAVING_STATUS);
      clearTimeout(savingIndicatorTimeoutRef.current);
      dispatch(editQuizQuestion({
        patch,
        id: currentQuestion.id,
      })).then((res) => {
        if (isEmpty(res.error)) {
          setSavingStatus(SavingIndicator.SUCCESS_STATUS);
        } else {
          setSavingStatus(SavingIndicator.ERROR_STATUS);
        }
      }).finally(() => {
        unregister();
        savingIndicatorTimeoutRef.current = setTimeout(() => {
          setSavingStatus(SavingIndicator.HIDDEN_STATUS);
        }, 2000);
      });
    }
  };

  const handleOnClick = (e) => {
    if (e.target.tagName === 'IMG') {
      setShowFullScreen(true);
      setImageSrc(e.target.src);
    }
  };

  React.useEffect(() => {
    mountedRef.current = true;
  }, []);

  const getPlaceholder = () => t.FROALA.TEXT_PLACEHOLDER();

  const normalizeLists = (container, tagName) => {
    const tags = container.querySelectorAll(tagName.toLowerCase());
    if (tags.length <= 1) return;

    const extraTags = tags.length - 1;
    // an extra tag give an indent in redactor editor, while it is equalent to 20px margin in froala editor
    const marginLeft = extraTags * 20;

    // removing extra UL or OLs
    tags.forEach((tag) => {
      while (tag.firstChild) {
        container.appendChild(tag.firstChild);
      }
      tag.remove();
    });

    // setting required margin corresponding to indend
    container.style.marginLeft = `${marginLeft}px`;
  };

  /**
   * Giving an indent in the Redactor editor (the editor used in the old quiz UI)
   * adds an extra <ul> or <ol> element. In the Froala editor, this extra
   * <ul> or <ol> is processed in an undesired way. To rectify this issue,
   * pre-processing is applied to the value before passing it to Froala.
   * Each indent in the Redactor editor is equivalent to a 20px margin in the Froala editor.
   * So, we eliminate the nested extra <ul> or <ol> elements and
   * apply a margin of (number of removed tags * 20px) to the parent element.
   *
   * this processing will be needed only for the existing old UI All-question-view quiz,
   * in future we dont need this processing, we can pass value directly to froala
   * without processing
   */
  const processNestedListsHtml = (htmlString: string) => {
    const parser = new DOMParser();
    // coverting html string to html doc
    const doc = parser.parseFromString(htmlString, 'text/html');
    // function to find UL or OL element in doc and remove the nested tags(UL or OL)
    function flattenLists(children: HTMLCollection) {
      Array.from(children).forEach((child) => {
        if (child.tagName === 'UL') {
          normalizeLists(child, 'UL');
        } else if (child.tagName === 'OL') {
          normalizeLists(child, 'OL');
        } else {
          flattenLists(child.children);
        }
      });
    }
    flattenLists(doc.body.children);
    return doc.body.innerHTML;
  };
  const appendSuffix = (htmlString: string, suffix: string) => {
    if (!suffix) return htmlString;

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    const firstParagraph = doc.querySelector('p');

    if (firstParagraph) {
      firstParagraph.insertAdjacentHTML('beforeend', suffix);
    } else {
      // Fallback: append to the end of the content if no <p> is found
      doc.body.insertAdjacentHTML('beforeend', suffix);
    }

    return doc.body.innerHTML;
  };

  if (isEditMode) {
    return (
      <NvFroala
        allowToolbar
        value={processNestedListsHtml(value)}
        preset={FroalaViewMode.NORMAL}
        uploadType={UploadType.NONE}
        minHeight={80}
        withCodeView
        className={className}
        onChange={handleChange}
        placeholder={getPlaceholder()}
        dataQa={dataQaQuestionSetAttributes.questionModal.questionTitle}
        dataQaId={`${dataQaQuestionSetAttributes.questionModal.questionTitle}_${currentQuestion.id}`}
      />
    );
  }

  const isRequiredSurvey = isCurrentQuestionRequired() && isCurrentActivityType(ComponentType.SURVEY);
  const starSuffix = isRequiredSurvey
    ? renderToStaticMarkup(<RequiredAsterisk />)
    : null;

  return (
    <React.Fragment>
      <ResponsivelyEmbeddedAngularHTML
        className={className}
        angularServices={angularServices}
        template={starSuffix ? appendSuffix(processNestedListsHtml(currentQuestion.questionText ?? ''), starSuffix) : processNestedListsHtml(currentQuestion.questionText ?? '')}
        onClick={handleOnClick}
        id={getQuestionLabelElementId(currentQuestion.id)}
      />
      <NvModal
        type={ModalType.FULL}
        show={showFullScreen}
        onClose={() => setShowFullScreen(false)}
        header={t.LECTURE_PAGES.COMPONENTS.CONTENT_TEMPLATES.LABELS.FULL_SCREEN()}
        body={(
          <div className='w-100 h-100'>
            <img className='w-100' src={imageSrc} alt='' />
          </div>
        )}
      />
    </React.Fragment>
  );
};

export default QuizQuestionContent;
